import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import AvatarDisplay from '../components/AvatarDisplay';
import { postService } from '../services/postService';
import UserENSRecords from '../components/UserENSRecords';

const ProfilePage = () => {
  const { address } = useParams();
  const navigate = useNavigate();
  const { address: currentUserAddress, ensName: currentUserEns } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);

  const profileAddress = address || currentUserAddress;

  console.log('🔥 ProfilePage rendered, address param:', useParams().address);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/users/${profileAddress}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('zhat_token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setProfile(data.user);
          setFollowersCount(data.user.follower_count || 0);
          setFollowingCount(data.user.following_count || 0);
        } else {
          console.error('Failed to fetch profile');
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [profileAddress]);

  useEffect(() => {
    if (!profileAddress || !currentUserAddress) return;
    // Skip follow check for own profile
    if (profileAddress.toLowerCase() === currentUserAddress.toLowerCase()) return;
    const checkFollow = async () => {
      const isFollowing = await postService.checkFollowStatus(profileAddress);
      setFollowing(isFollowing);
    };
    checkFollow();
  }, [profileAddress, currentUserAddress]);

  const handleFollow = async () => {
    if (followLoading) return;
    setFollowLoading(true);
    try {
      if (following) {
        await postService.unfollowUser(profileAddress);
        setFollowing(false);
        setFollowersCount(prev => Math.max(0, prev - 1));
      } else {
        await postService.followUser(profileAddress);
        setFollowing(true);
        setFollowersCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Follow action failed:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container" style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="page-container">
        <h2>User not found</h2>
      </div>
    );
  }

  // Case‑insensitive own‑profile check
  const isOwnProfile = profileAddress?.toLowerCase() === currentUserAddress?.toLowerCase();

  return (
    <div className="page-container">
      <div className="profile-header" style={{ display: 'flex', alignItems: 'center', gap: '2rem', marginBottom: '2rem' }}>
        <AvatarDisplay ensName={profile.ens_name} address={profile.eth_address} size={80} />
        <div>
          <h2 style={{ color: 'var(--gold)', margin: 0 }}>{profile.display_name || profile.ens_name}</h2>
          <p style={{ color: 'var(--text-secondary)' }}>{profile.ens_name}</p>
          <div style={{ display: 'flex', gap: '2rem', marginTop: '1rem' }}>
            <div><strong>{followersCount}</strong> Followers</div>
            <div><strong>{followingCount}</strong> Following</div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', alignItems: 'center' }}>
            {!isOwnProfile && (
              <button
                onClick={handleFollow}
                disabled={followLoading}
                className={`btn ${following ? 'btn-dark-red' : 'btn-gold'}`}
                style={{ minWidth: '120px' }}
              >
                {followLoading ? '...' : following ? 'Unfollow' : 'Follow'}
              </button>
            )}
            {isOwnProfile && (
              <button
                onClick={() => navigate('/home/identity')}
                className="btn btn-gold"
                style={{ minWidth: '120px' }}
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ENS Profile Records – only show if the user has an ENS name */}
      {profile.ens_name && <UserENSRecords ensName={profile.ens_name} />}

      {/* You can add a list of user's posts here later */}
    </div>
  );
};

export default ProfilePage;
